import React, { useState } from 'react';
import StepOneBasic from './StepOneBasic';
import StepTwoProject from './StepTwoProject';
import StepThreeDesign from './StepThreeDesign';
import StepFourSchedule from './StepFourSchedule';
import StepFiveExtra from './StepFiveExtra';

const ConsultationForm = ({ isOpen, onClose, onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Basic Details
    fullName: '',
    phoneNumber: '',
    emailAddress: '',
    cityLocation: '',
    
    // Step 2: Project Details
    projectType: '',
    propertyType: '',
    areaSize: '',
    budgetRange: '',
    
    // Step 3: Design Preferences
    stylePreference: '',
    colorPreference: '',
    referenceImage: null,
    
    // Step 4: Scheduling
    preferredDate: '',
    preferredTime: '',
    
    // Step 5: Additional Info
    messageRequirements: '',
    contactWhatsApp: false,
    siteVisitRequired: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const totalSteps = 5;

  const updateFormData = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.fullName.trim()) {
          newErrors.fullName = 'Full name is required';
        } else if (formData.fullName.trim().length < 2) {
          newErrors.fullName = 'Name must be at least 2 characters';
        }

        if (!formData.phoneNumber.trim()) {
          newErrors.phoneNumber = 'Phone number is required';
        } else if (!/^[0-9+\-\s()]+$/.test(formData.phoneNumber)) {
          newErrors.phoneNumber = 'Please enter a valid phone number';
        } else if (formData.phoneNumber.replace(/\D/g, '').length < 10) {
          newErrors.phoneNumber = 'Phone number must be at least 10 digits';
        }

        if (!formData.emailAddress.trim()) {
          newErrors.emailAddress = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
          newErrors.emailAddress = 'Please enter a valid email address';
        }

        if (!formData.cityLocation.trim()) {
          newErrors.cityLocation = 'City/Location is required';
        }
        break;

      case 2:
        if (!formData.projectType) {
          newErrors.projectType = 'Project type is required';
        }
        if (!formData.propertyType) {
          newErrors.propertyType = 'Property type is required';
        }
        if (!formData.areaSize.trim()) {
          newErrors.areaSize = 'Area size is required';
        } else if (isNaN(formData.areaSize) || formData.areaSize <= 0) {
          newErrors.areaSize = 'Please enter a valid area size';
        }
        if (!formData.budgetRange) {
          newErrors.budgetRange = 'Budget range is required';
        }
        break;

      case 3:
        if (!formData.stylePreference) {
          newErrors.stylePreference = 'Style preference is required';
        }
        break;

      case 4:
        if (!formData.preferredDate) {
          newErrors.preferredDate = 'Preferred date is required';
        }
        if (!formData.preferredTime) {
          newErrors.preferredTime = 'Preferred time is required';
        }
        break;

      case 5:
        if (!formData.siteVisitRequired) {
          newErrors.siteVisitRequired = 'Site visit preference is required';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      const consultationData = {
        ...formData,
        submittedAt: new Date().toISOString(),
        id: Date.now()
      };

      onSubmit(consultationData);
      setShowSuccess(true);

      // Reset form after success
      setTimeout(() => {
        setFormData({
          fullName: '',
          phoneNumber: '',
          emailAddress: '',
          cityLocation: '',
          projectType: '',
          propertyType: '',
          areaSize: '',
          budgetRange: '',
          stylePreference: '',
          colorPreference: '',
          referenceImage: null,
          preferredDate: '',
          preferredTime: '',
          messageRequirements: '',
          contactWhatsApp: false,
          siteVisitRequired: ''
        });
        setCurrentStep(1);
        setShowSuccess(false);
        onClose();
      }, 3000);

    } catch (error) {
      console.error('Error submitting consultation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      fullName: '',
      phoneNumber: '',
      emailAddress: '',
      cityLocation: '',
      projectType: '',
      propertyType: '',
      areaSize: '',
      budgetRange: '',
      stylePreference: '',
      colorPreference: '',
      referenceImage: null,
      preferredDate: '',
      preferredTime: '',
      messageRequirements: '',
      contactWhatsApp: false,
      siteVisitRequired: ''
    });
    setCurrentStep(1);
    setErrors({});
    onClose();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepOneBasic
            data={formData}
            onChange={updateFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <StepTwoProject
            data={formData}
            onChange={updateFormData}
            errors={errors}
          />
        );
      case 3:
        return (
          <StepThreeDesign
            data={formData}
            onChange={updateFormData}
            errors={errors}
          />
        );
      case 4:
        return (
          <StepFourSchedule
            data={formData}
            onChange={updateFormData}
            errors={errors}
          />
        );
      case 5:
        return (
          <StepFiveExtra
            data={formData}
            onChange={updateFormData}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'}}>
        
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-stone-200">
          <h2 className="text-2xl font-bold" style={{color: '#3E3E3E'}}>Book Consultation</h2>
          <button
            onClick={handleClose}
            className="text-stone-400 hover:text-stone-600 transition-colors"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            {[...Array(totalSteps)].map((_, index) => (
              <React.Fragment key={index}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                    index + 1 <= currentStep
                      ? 'text-white'
                      : 'text-stone-400 bg-stone-200'
                  }`}
                  style={{
                    backgroundColor: index + 1 <= currentStep ? '#C8A96A' : undefined
                  }}
                >
                  {index + 1}
                </div>
                {index < totalSteps - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 transition-all duration-300 ${
                      index + 1 < currentStep ? 'bg-primary' : 'bg-stone-200'
                    }`}
                    style={{
                      backgroundColor: index + 1 < currentStep ? '#C8A96A' : undefined
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold" style={{color: '#3E3E3E'}}>
              {currentStep === 1 && 'Basic Details'}
              {currentStep === 2 && 'Project Details'}
              {currentStep === 3 && 'Design Preferences'}
              {currentStep === 4 && 'Scheduling'}
              {currentStep === 5 && 'Additional Information'}
            </h3>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6">
          {showSuccess ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <span className="material-symbols-outlined text-3xl text-green-600">check_circle</span>
              </div>
              <h3 className="text-2xl font-bold mb-2" style={{color: '#3E3E3E'}}>Consultation Booked!</h3>
              <p className="text-stone-600 mb-4">We'll contact you within 24 hours to confirm your consultation.</p>
              <p className="text-sm text-stone-500">Redirecting...</p>
            </div>
          ) : (
            <>
              {renderStep()}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                    currentStep === 1
                      ? 'text-stone-400 cursor-not-allowed'
                      : 'border border-stone-300 text-stone-700 hover:bg-stone-50'
                  }`}
                >
                  Back
                </button>

                {currentStep === totalSteps ? (
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="px-8 py-2 rounded-full font-medium transition-all duration-300 text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{backgroundColor: '#C8A96A'}}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="material-symbols-outlined animate-spin">refresh</span>
                        Submitting...
                      </span>
                    ) : (
                      'Submit Consultation'
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    className="px-8 py-2 rounded-full font-medium transition-all duration-300 text-white hover:opacity-90"
                    style={{backgroundColor: '#C8A96A'}}
                  >
                    Next
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultationForm;
